import React, { useState, useEffect } from 'react';
import { useFirebase } from '../../context/FirebaseContext';
import { useTradeHistoryWebhookEnabled } from '../../context/FeatureFlagContext';
import { updateAccountWebhookEnabled, regenerateAccountWebhookSecret } from '../../api/journal';
import { getApiOrigin } from '../../api/core';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import WebhookIcon from '@mui/icons-material/Webhook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import './AccountSettingsModal.css';

/**
 * AccountSettingsModal - settings modal for journal accounts
 * Similar to PipelineSettingsModal for validation pipelines
 */
const AccountSettingsModal = ({ account, open, onClose, onAccountUpdate }) => {
  const { auth } = useFirebase();
  const webhookFeatureEnabled = useTradeHistoryWebhookEnabled();

  const [webhookEnabled, setWebhookEnabled] = useState(account?.webhook_enabled || false);
  const [webhookUrl, setWebhookUrl] = useState(account?.webhookUrl || '');
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // URL query parameter options
  const [includeDetails, setIncludeDetails] = useState(false);

  // Reset state when account changes
  useEffect(() => {
    if (account) {
      setWebhookEnabled(account.webhook_enabled || false);
      setWebhookUrl(account.webhookUrl || '');
    }
  }, [account]);

  if (!open || !account) return null;

  const handleToggleWebhook = async () => {
    if (webhookLoading) return;
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await updateAccountWebhookEnabled(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookEnabled(result.webhook_enabled);
          setWebhookUrl(result.webhookUrl);
          if (onAccountUpdate) onAccountUpdate(result);
        }
      }, account.account_id, !webhookEnabled);
    } catch (e) {
      setWebhookError(e.message || 'Failed to update webhook settings');
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleRegenerateSecret = async () => {
    if (webhookLoading) return;
    if (!window.confirm('Are you sure you want to regenerate the webhook secret? This will invalidate the current URL.')) {
      return;
    }
    setWebhookLoading(true);
    setWebhookError(null);

    try {
      await regenerateAccountWebhookSecret(auth, (result) => {
        if (result.error) {
          setWebhookError(result.error);
        } else {
          setWebhookUrl(result.webhookUrl);
          if (onAccountUpdate) onAccountUpdate({ ...account, webhookUrl: result.webhookUrl });
        }
      }, account.account_id);
    } catch (e) {
      setWebhookError(e.message || 'Failed to regenerate secret');
    } finally {
      setWebhookLoading(false);
    }
  };

  const getFullWebhookUrl = (withQueryParams = true) => {
    if (!webhookUrl) return '';
    // If backend returns relative URL, prepend the API origin
    let url = webhookUrl.startsWith('/') ? `${getApiOrigin()}${webhookUrl}` : webhookUrl;
    if (withQueryParams && includeDetails) {
      url += '?details=true';
    }
    return url;
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(getFullWebhookUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      setWebhookError('Failed to copy to clipboard');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="account-settings-overlay" onClick={handleOverlayClick}>
      <div className="account-settings-modal">
        <div className="account-settings-header">
          <h3>Account Settings</h3>
          <button className="account-settings-close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="account-settings-content">
          {/* Account Info */}
          <div className="account-settings-section">
            <label className="account-settings-label">Account</label>
            <div className="account-info-display">
              <span className="account-info-name">{account.name}</span>
              {account.description && (
                <span className="account-info-description">{account.description}</span>
              )}
            </div>
          </div>

          {/* Webhook Section */}
          {webhookFeatureEnabled && (
            <div className="account-settings-section webhook-section">
              <div className="webhook-header">
                <WebhookIcon className="webhook-icon" />
                <label className="account-settings-label">Webhook Integration</label>
                <button
                  className="webhook-help-btn"
                  onClick={() => setShowHelp(!showHelp)}
                  title="Help"
                >
                  <HelpOutlineIcon fontSize="small" />
                </button>
              </div>

              {webhookError && <div className="account-settings-error">{webhookError}</div>}

              <div className="webhook-toggle-row">
                <span className="webhook-toggle-label">
                  {webhookEnabled ? 'Webhook enabled' : 'Webhook disabled'}
                </span>
                <button
                  className={`webhook-toggle ${webhookEnabled ? 'enabled' : ''}`}
                  onClick={handleToggleWebhook}
                  disabled={webhookLoading}
                >
                  <span className="webhook-toggle-slider" />
                </button>
              </div>

              {webhookEnabled && (
                <div className="webhook-url-section">
                  <label className="webhook-url-label">Webhook URL</label>
                  <div className="webhook-url-row">
                    <input
                      type="text"
                      value={getFullWebhookUrl()}
                      readOnly
                      className="webhook-url-input"
                    />
                    <button
                      className={`webhook-url-btn ${copied ? 'copied' : ''}`}
                      onClick={handleCopyUrl}
                      title="Copy URL"
                    >
                      {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </button>
                    <button
                      className="webhook-url-btn regenerate"
                      onClick={handleRegenerateSecret}
                      disabled={webhookLoading}
                      title="Regenerate secret"
                    >
                      <RefreshIcon fontSize="small" />
                    </button>
                  </div>

                  {/* URL Options */}
                  <div className="webhook-options">
                    <label className="webhook-option">
                      <input
                        type="checkbox"
                        checked={includeDetails}
                        onChange={(e) => setIncludeDetails(e.target.checked)}
                      />
                      <span className="webhook-option-text">
                        <strong>details</strong> - Include full trade entry details
                      </span>
                    </label>
                  </div>

                  <p className="webhook-url-hint">
                    Keep this URL secret. Use it to retrieve your trade history from external systems.
                  </p>
                </div>
              )}

              {showHelp && (
                <div className="webhook-help-card">
                  <h4>How to use the Trade History Webhook</h4>
                  <p>Send a POST request to retrieve your trade history:</p>
                  <pre className="webhook-help-code">
{`POST ${getFullWebhookUrl() || '/api/v2/webhook/trades/{accountId}/{secret}'}
Content-Type: application/json

{
  "limit": 50,
  "includeStats": true
}`}
                  </pre>
                  <p>Response:</p>
                  <pre className="webhook-help-code">
{`{
  "success": true,
  "accountId": ${account.account_id},
  "accountName": "${account.name}",
  "totalEntries": 42,
  "entries": [...],
  "statistics": {
    "total_entries": 42,
    "winning_trades": 28,
    "losing_trades": 12,
    "win_ratio": 70.0,
    "total_profit_loss": 3421.75
  },
  "remainingCalls": 95
}`}
                  </pre>
                  <p>
                    <strong>Options:</strong><br/>
                    <code>?details=true</code> - Include full entry details (notes, lessons, mood)
                  </p>
                  <p>
                    <strong>Rate Limits:</strong><br/>
                    Free: 10/day | Basic: 100/day | Pro: 1,000/day | Unlimited: No limit
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
